import { Box, Button, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { convertExistingVideos, getVideoJobs, retryVideoJob, VideoJobSummary } from "../../service/VideoService";
import { showSuccessToast } from "../../utils/Toast";
import { orangeButton, outlinedButton, StyledTableCell, StyledTableRow, TableWrapper } from "../logs/logStyles";

const REFRESH_MS = 10000;
const minutes = (seconds?: number) => (seconds ? `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}` : "-");

// Uploaded videos are converted to MP4 (H.264 + AAC, max 1080p) by the worker, so they play on every phone and
// browser. Here: how it's going, converting the videos from before, and retrying a failed conversion.
export default function VideoConversions() {
  const [t] = useTranslation("global");
  const [data, setData] = useState<VideoJobSummary | null>(null);

  const load = () => getVideoJobs().then(setData);
  useEffect(() => {
    load();
    const interval = setInterval(load, REFRESH_MS);
    return () => clearInterval(interval);
  }, []);

  const convertExisting = async () => {
    const result = await convertExistingVideos();
    if (result) {
      showSuccessToast(t("videoConversion.queued", { queued: result.queued, done: result.alreadyConverted }), false);
      load();
    }
  };

  return (
    <Box sx={{ marginBottom: "2%" }}>
      <Box sx={{ width: "100%", mt: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
        <Typography variant="h5" fontWeight="bold">
          {t("videoConversion.title")}
        </Typography>
        <Button variant="contained" sx={orangeButton} onClick={convertExisting}>
          {t("videoConversion.convertExisting")}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ width: "100%", mt: 1 }}>
        {t("videoConversion.intro")}
      </Typography>
      {data && (
        <Typography variant="body2" sx={{ width: "100%", mt: 1 }}>
          {t("videoConversion.counts", { queued: data.queued, processing: data.processing, done: data.done, failed: data.failed })}
        </Typography>
      )}
      {data?.agentEnabled && (
        <Typography variant="body2" color="text.secondary" sx={{ width: "100%", mt: 0.5 }}>
          {data.agents?.length
            ? data.agents.map((agent) => t(agent.online ? "videoConversion.agentOnline" : "videoConversion.agentOffline", { name: agent.name, encoder: agent.encoder || "cpu", gpu: agent.gpu ? `, ${agent.gpu}` : "", jobs: agent.jobsDone })).join(" · ")
            : t("videoConversion.noAgent")}
        </Typography>
      )}
      <TableWrapper component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>{t("videoConversion.time")}</StyledTableCell>
              <StyledTableCell>{t("videoConversion.status")}</StyledTableCell>
              <StyledTableCell>{t("videoConversion.source")}</StyledTableCell>
              <StyledTableCell>{t("videoConversion.length")}</StyledTableCell>
              <StyledTableCell>{t("videoConversion.details")}</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.latest || []).map((job) => (
              <StyledTableRow key={job.id}>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{new Date(job.updatedAt).toLocaleString("da-DK")}</TableCell>
                <TableCell>{t(`videoConversion.statuses.${job.status}`)}</TableCell>
                <TableCell>{t(`videoConversion.sources.${job.source}`)}</TableCell>
                <TableCell>{minutes(job.durationSeconds)}</TableCell>
                <TableCell>
                  {job.status === "done" && (
                    <a href={job.url} target="_blank" rel="noopener noreferrer" style={{ color: "#EB6608" }}>
                      {t("videoConversion.open")}
                    </a>
                  )}
                  {job.status === "failed" && (
                    <>
                      <Typography variant="caption" color="error.main" sx={{ display: "block", wordBreak: "break-all" }}>
                        {job.error.slice(-160)}
                      </Typography>
                      <Button size="small" variant="outlined" sx={outlinedButton} onClick={() => retryVideoJob(job.id).then(load)}>
                        {t("videoConversion.retry")}
                      </Button>
                    </>
                  )}
                </TableCell>
              </StyledTableRow>
            ))}
            {!data?.latest.length && (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: "center" }}>
                  {t("videoConversion.none")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>
    </Box>
  );
}
